/**
 * components/needs/FieldTypeDropdown.tsx
 *
 * Reusable dropdown for selecting field types with visual badges.
 * Used by NeedItem and CustomInformationModal to ensure consistent styling.
 *
 * Features:
 * - Colored badge with icon for current type
 * - Dropdown menu with icons and checkmark for active type
 * - Smart positioning (top/bottom based on available viewport space)
 * - Closes dropdown defensively when readOnly becomes true
 * - Click-outside handling
 * - Configurable type list (allows filtering out certain types)
 * - readOnly mode: renders a static non-interactive badge (for catalog items)
 *
 * Recent changes (Feb 2026):
 * - Renamed 'text' label from "Texte" to "Texte long" (textarea)
 * - Added 'short_text' type with Type icon, label "Texte court" (single-line input)
 * - Added readOnly prop for locked catalog items (no dropdown, no ChevronDown)
 * - Guarded click-outside and position useEffects with readOnly bail-out
 * Updated Apr 2026: added 'info' type (display-only informational message).
 */

import { useState, useRef, useEffect } from 'react';
import {
  FileText,
  MessageSquare,
  Type,
  Calendar,
  Circle,
  ListChecks,
  Check,
  ChevronDown,
  Info,
} from 'lucide-react';

type FieldType = 'document' | 'text' | 'short_text' | 'date' | 'single_choice' | 'multiple_choice' | 'info';

interface FieldTypeDropdownProps {
  value: FieldType;
  onChange: (type: FieldType) => void;
  types: FieldType[];
  readOnly?: boolean;
}

const TYPE_CONFIG = {
  document: {
    icon: FileText,
    label: 'Document',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
  },
  short_text: {
    icon: Type,
    label: 'Texte court',
    color: 'bg-sky-100 text-sky-700 border-sky-200',
  },
  text: {
    icon: MessageSquare,
    label: 'Texte long',
    color: 'bg-primary-100 text-primary-700 border-primary-200',
  },
  date: {
    icon: Calendar,
    label: 'Date',
    color: 'bg-amber-100 text-amber-700 border-amber-200',
  },
  single_choice: {
    icon: Circle,
    label: 'Choix unique',
    color: 'bg-teal-100 text-teal-700 border-teal-200',
  },
  multiple_choice: {
    icon: ListChecks,
    label: 'Choix multiples',
    color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  },
  info: {
    icon: Info,
    label: 'Message informatif',
    color: 'bg-primary-100 text-primary-700 border-primary-200',
  },
};

export default function FieldTypeDropdown({
  value,
  onChange,
  types,
  readOnly = false,
}: FieldTypeDropdownProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const currentConfig = TYPE_CONFIG[value];
  const CurrentIcon = currentConfig.icon;

  // Close dropdown when clicking outside
  useEffect(() => {
    if (readOnly) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [readOnly]);

  useEffect(() => {
    if (readOnly) setShowDropdown(false);
  }, [readOnly]);

  // Calculate dropdown position when it opens
  useEffect(() => {
    if (readOnly) return;
    if (showDropdown && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = 280;
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;

      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
    }
  }, [showDropdown, readOnly]);

  const handleTypeSelect = (type: FieldType) => {
    onChange(type);
    setShowDropdown(false);
  };

  if (readOnly) {
    return (
      <span
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium cursor-default select-none ${currentConfig.color}`}
        title="Type de champ (prédéfini)"
      >
        <CurrentIcon className="w-4 h-4" />
        <span>{currentConfig.label}</span>
      </span>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setShowDropdown(!showDropdown)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all ${currentConfig.color} hover:opacity-80 hover:shadow-sm`}
        title="Cliquer pour changer le type"
      >
        <CurrentIcon className="w-4 h-4" />
        <span>{currentConfig.label}</span>
        <ChevronDown className="w-3.5 h-3.5" />
      </button>

      {/* Type dropdown */}
      {showDropdown && (
        <div
          className={`absolute left-0 w-48 bg-white rounded-xl shadow-soft-xl border border-neutral-200 py-2 z-10 ${
            dropdownPosition === 'bottom' ? 'top-full mt-2' : 'bottom-full mb-2'
          }`}
        >
          {types.map((type) => {
            const config = TYPE_CONFIG[type];
            const Icon = config.icon;

            return (
              <button
                key={type}
                type="button"
                onClick={() => handleTypeSelect(type)}
                className={`w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-neutral-50 transition-colors ${
                  value === type
                    ? 'text-primary-600 font-medium bg-primary-50'
                    : 'text-neutral-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                {config.label}
                {value === type && <Check className="w-4 h-4 ml-auto" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
